import React from 'react';
import { Scan, Shield, Database, Cloud, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Dimensions = () => {
  const { t } = useLanguage();

  const dimensions = [
    {
      icon: Scan,
      title: t.dimensions.external.title,
      points: t.dimensions.external.points,
      gradient: 'from-cyan-600 to-blue-600',
      bgGradient: 'from-cyan-950/50 to-blue-950/30'
    },
    {
      icon: Database,
      title: t.dimensions.internal.title,
      points: t.dimensions.internal.points,
      gradient: 'from-cyan-600 to-sky-600',
      bgGradient: 'from-cyan-950/50 to-sky-950/30'
    },
    {
      icon: Shield,
      title: t.dimensions.darknet.title,
      points: t.dimensions.darknet.points,
      gradient: 'from-blue-600 to-cyan-600',
      bgGradient: 'from-blue-950/50 to-cyan-950/30'
    },
    {
      icon: Cloud,
      title: t.dimensions.cloud.title,
      points: t.dimensions.cloud.points,
      gradient: 'from-sky-600 to-blue-600',
      bgGradient: 'from-sky-950/50 to-blue-950/30'
    }
  ];

  return (
    <section id="dimensions" className="py-24 bg-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-0 w-96 h-96 bg-cyan-600/5 rounded-full blur-3xl" />
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
            {t.dimensions.title}
          </h2>
          <p className="text-xl text-gray-400 max-w-4xl mx-auto">
            {t.dimensions.subtitle}
          </p>
        </div>

        {/* Dimensions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
          {dimensions.map((dimension, index) => {
            const Icon = dimension.icon;
            return (
              <div
                key={index}
                className="group relative"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                {/* Glow Effect */}
                <div className={`absolute -inset-1 bg-gradient-to-r ${dimension.gradient} rounded-lg blur opacity-20 group-hover:opacity-40 transition duration-300`} />
                
                {/* Card */}
                <div className={`relative bg-gradient-to-br ${dimension.bgGradient} border border-cyan-500/30 rounded-lg p-8 h-full transition-all duration-300 group-hover:scale-105 group-hover:border-cyan-500/50`}>
                  {/* Icon */}
                  <div className="mb-6">
                    <div className={`w-16 h-16 rounded-lg bg-gradient-to-br ${dimension.gradient} p-3 flex items-center justify-center`}>
                      <Icon className="w-10 h-10 text-white" strokeWidth={1.5} />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-white mb-6">
                    {dimension.title}
                  </h3>

                  {/* Points */}
                  <ul className="space-y-4">
                    {dimension.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 leading-relaxed">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Dimensions;
